import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, Calendar, Clock, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Reveal } from '../hooks/useReveal';

const IMG_DIST = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/3wifgtrh_WhatsApp%20Image%202024-11-17%20at%2021.04.50.jpeg';
const IMG_POWER = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/n2sltbd7_WhatsApp%20Image%202024-11-17%20at%2021.04.45%20%281%29.jpeg';
const IMG_OVEN = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/gfe4xo80_WhatsApp%20Image%202024-11-17%20at%2021.04.49%20%281%29.jpeg';
const IMG_CONTAINER = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/zcgpbexp_1746633955032.jpg';
const IMG_TRUCK = 'https://customer-assets.emergentagent.com/job_voltage-solutions-2/artifacts/ju8kbuwj_1746633956430.jpg';

const blogPosts = {
  tr: [
    { id: 1, title: 'Dagitim Trafolari: Kapsamli Bir Rehber', excerpt: 'Dagitim trafolari, elektrik sebekelerinin en temel bilesenleridir. 50 ile 2500 kVA araliginda guc saglarlar ve elektrik enerjisinin son tuketiciye ulasmasinda kritik rol oynarlar.', category: 'Teknik', date: '2024-12-15', readTime: '5 dk', img: IMG_DIST },
    { id: 2, title: 'Guc Trafolari ve Endustriyel Uygulamalar', excerpt: 'Guc trafolari, 2500 kVA\'dan 16.000 kVA\'ya kadar yuksek guc araliginda calisarak endustriyel tesisler ve enerji dagitim merkezleri icin vazgecilmez cozumler sunar.', category: 'Endustri', date: '2024-11-20', readTime: '7 dk', img: IMG_POWER },
    { id: 3, title: 'Trafo Uretim Surecinde Vakum Kurutma', excerpt: 'Vakum kurutma, trafo uretiminin en kritik asamalarindan biridir. Cekirdek-bobin montaji tamamlandiktan sonra termostatik kontrol altinda vakum firinlarinda kurutulur.', category: 'Uretim', date: '2024-10-08', readTime: '4 dk', img: IMG_OVEN },
    { id: 4, title: 'Uluslararasi Lojistik ve Trafo Sevkiyati', excerpt: 'Astravolt olarak urettigimiz trafolari uluslararasi standartlarda paketleyerek dunyanin dort bir yanina guvenli sekilde sevk ediyoruz.', category: 'Lojistik', date: '2024-09-25', readTime: '3 dk', img: IMG_CONTAINER },
    { id: 5, title: 'Kalite Kontrol: ISO Standartlarimiz', excerpt: 'ISO 9001, ISO 14001 ve ISO 45001 sertifikalari ile uretim surecimizin her asamasinda en yuksek kalite standartlarini sagliyoruz.', category: 'Kalite', date: '2024-08-12', readTime: '6 dk', img: IMG_TRUCK },
  ],
  en: [
    { id: 1, title: 'Distribution Transformers: A Comprehensive Guide', excerpt: 'Distribution transformers are fundamental components of electrical networks. Ranging from 50 to 2500 kVA, they play a critical role in delivering electrical energy to end consumers.', category: 'Technical', date: '2024-12-15', readTime: '5 min', img: IMG_DIST },
    { id: 2, title: 'Power Transformers and Industrial Applications', excerpt: 'Power transformers operate in the high power range from 2,500 to 16,000 kVA, providing indispensable solutions for industrial facilities and energy distribution centers.', category: 'Industry', date: '2024-11-20', readTime: '7 min', img: IMG_POWER },
    { id: 3, title: 'Vacuum Drying in Transformer Production', excerpt: 'Vacuum drying is one of the most critical stages of transformer production. After core-coil assembly, units are dried in thermostatically controlled vacuum ovens.', category: 'Production', date: '2024-10-08', readTime: '4 min', img: IMG_OVEN },
    { id: 4, title: 'International Logistics and Transformer Shipping', excerpt: 'At Astravolt, we package our transformers to international standards and safely ship them to all corners of the world.', category: 'Logistics', date: '2024-09-25', readTime: '3 min', img: IMG_CONTAINER },
    { id: 5, title: 'Quality Control: Our ISO Standards', excerpt: 'With ISO 9001, ISO 14001, and ISO 45001 certifications, we ensure the highest quality standards at every stage of our production process.', category: 'Quality', date: '2024-08-12', readTime: '6 min', img: IMG_TRUCK },
  ],
};

const blogLabels = {
  tr: { title: 'Blog', subtitle: 'Trafo teknolojisi, uretim surecleri ve endustri haberleri hakkinda en guncel icerikler.', readMore: 'Devamini Oku', allPosts: 'Tum Yazilar' },
  en: { title: 'Blog', subtitle: 'Latest content about transformer technology, production processes, and industry news.', readMore: 'Read More', allPosts: 'All Posts' },
  fr: { title: 'Blog', subtitle: 'Contenu sur la technologie des transformateurs et les processus de production.', readMore: 'Lire la Suite', allPosts: 'Tous les Articles' },
  es: { title: 'Blog', subtitle: 'Contenido sobre tecnologia de transformadores y procesos de produccion.', readMore: 'Leer Mas', allPosts: 'Todos los Articulos' },
  ar: { title: 'المدونة', subtitle: 'احدث المحتويات حول تقنية المحولات وعمليات الانتاج.', readMore: 'اقرا المزيد', allPosts: 'جميع المقالات' },
  fa: { title: 'وبلاگ', subtitle: 'جدیدترین مطالب درباره فناوری ترانسفورمر و فرآیندهای تولید.', readMore: 'بیشتر بخوانید', allPosts: 'همه مقالات' },
};

export default function Blog() {
  const { language } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState('all');

  const labels = blogLabels[language] || blogLabels.en;
  const posts = blogPosts[language] || blogPosts.en;
  const categories = ['all', ...new Set(posts.map(p => p.category))];
  const filtered = selectedCategory === 'all' ? posts : posts.filter(p => p.category === selectedCategory);

  return (
    <div data-testid="blog-page">
      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-[#05060A] grid-pattern">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs text-blue-400 uppercase tracking-[0.2em] font-medium mb-3">Astravolt</p>
          <h1 data-testid="blog-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white mb-4">
            {labels.title}
          </h1>
          <p className="text-base md:text-lg text-slate-300 max-w-xl">{labels.subtitle}</p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-[#0B0E14] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                data-testid={`blog-filter-${cat}`}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-sm transition-all ${
                  selectedCategory === cat
                    ? 'bg-blue-600 text-white'
                    : 'bg-[#05060A] text-slate-400 border border-white/10 hover:text-white hover:border-white/20'
                }`}
              >
                {cat === 'all' ? labels.allPosts : cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-20 md:py-32 bg-[#0B0E14]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((post, i) => (
              <Reveal key={post.id}>
                <article
                  data-testid={`blog-post-${post.id}`}
                  className="group border border-white/10 bg-[#05060A] overflow-hidden hover:bg-[#111620] transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={post.img}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#05060A] to-transparent" />
                    <span className="absolute top-4 left-4 bg-blue-600/90 text-white text-xs font-bold px-3 py-1 rounded-sm uppercase tracking-wider">
                      {post.category}
                    </span>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 mb-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {post.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {post.readTime}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-3 tracking-tight line-clamp-2 group-hover:text-blue-400 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-sm text-slate-400 leading-relaxed line-clamp-3 mb-4">
                      {post.excerpt}
                    </p>
                    <span className="inline-flex items-center gap-2 text-blue-400 text-sm font-medium group-hover:gap-3 transition-all cursor-pointer">
                      {labels.readMore}
                      <ChevronRight className="w-4 h-4" />
                    </span>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#05060A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <Link to="/contact">
              <Button data-testid="blog-contact-cta" className="bg-blue-600 text-white rounded-sm px-10 py-6 font-bold uppercase tracking-widest hover:bg-blue-500 text-sm">
                Get In Touch <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
